import { useParams } from "react-router-dom";
import { Button, Card, CardHeader, ServiceMark, StatusPill } from "@/components/primitives";
import { Icon } from "@/components/Icon";
import { PageHeader } from "@/components/Layout";
import { UptimeRibbon } from "@/components/UptimeRibbon";
import { IncidentDetail } from "@/components/IncidentDetail";
import { ServiceRegions } from "@/components/ServiceRegions";
import { useSnapshot } from "@/lib/snapshot-context";

export function ServiceDetail() {
  const { serviceId = "" } = useParams();
  const { data } = useSnapshot();
  const svc = data.services.find((s) => s.id === serviceId);
  if (!svc) return <div style={{ padding: 32 }}>Service not found.</div>;

  const allIncidents = data.incidents.filter((i) => i.serviceId === serviceId);
  const active = allIncidents.filter((i) => i.status !== "resolved");
  const resolved = allIncidents.filter((i) => i.status === "resolved");
  const uptime = data.uptime[serviceId] ?? [];

  const affectedSet = new Set<string>();
  for (const inc of active) for (const c of inc.components) affectedSet.add(c);

  return (
    <>
      <PageHeader crumbs={["Status Watch", "Services", svc.name]} />
      <div
        style={{
          padding: "20px 32px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <Card style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <ServiceMark service={svc} size={56} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 24,
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {svc.name}
                </h1>
                <StatusPill state={svc.state} size="lg" />
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--muted-foreground)",
                  marginTop: 4,
                }}
              >
                {svc.summary}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 18,
                  marginTop: 14,
                  flexWrap: "wrap",
                }}
              >
                <MiniStat label="90-day uptime" value={`${svc.uptime90}%`} />
                <MiniStat
                  label="Source"
                  value={svc.source === "rss" ? "RSS feed" : "Webhook"}
                  icon={svc.source === "rss" ? "rss" : "webhook"}
                />
                <MiniStat label="Poll interval" value={svc.pollInterval} />
                <MiniStat
                  label="Components"
                  value={`${svc.components.length} tracked`}
                />
                <MiniStat label="Last check" value={svc.lastChecked} />
                {svc.monitoredRegions && (
                  <MiniStat
                    label="Monitored regions"
                    value={svc.monitoredRegions.join(", ") || "All regions"}
                  />
                )}
                <MiniStat
                  label="Added by"
                  value={svc.addedBy ? `${svc.addedBy} · ${svc.addedAt}` : svc.addedAt}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                alignItems: "flex-end",
              }}
            >
              <Button
                variant="outline"
                icon="external"
                href={`https://${svc.domain}`}
                title="Open provider status page"
              >
                Provider status page
              </Button>
            </div>
          </div>
        </Card>

        <ServiceRegions key={svc.id} service={svc} />

        <Card>
          <CardHeader
            title="Uptime — last 90 days"
            description={`${svc.uptime90}% of the last 90 days operational. ${allIncidents.length} incidents recorded.`}
            right={
              <div style={{ display: "flex", gap: 6 }}>
                <Button size="sm" variant="ghost">
                  7d
                </Button>
                <Button size="sm" variant="ghost">
                  30d
                </Button>
                <Button size="sm" variant="outline">
                  90d
                </Button>
              </div>
            }
          />
          <div style={{ padding: 16 }}>
            <UptimeRibbon uptime={uptime} height={36} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 8,
                fontSize: 10,
                color: "var(--muted-foreground)",
                fontFamily: "var(--font-mono)",
              }}
            >
              <span>90 days ago</span>
              <span>60 days ago</span>
              <span>30 days ago</span>
              <span>today</span>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Tracked components"
            description={`${svc.components.length} components reported by provider`}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 0,
            }}
          >
            {svc.components.map((c, i) => {
              const isAffected = affectedSet.has(c);
              return (
                <div
                  key={c}
                  style={{
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    borderTop: i >= 3 ? "1px solid var(--border)" : "none",
                    borderRight:
                      i % 3 !== 2 ? "1px solid var(--border)" : "none",
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: isAffected
                        ? "var(--warning)"
                        : "var(--success)",
                      boxShadow: isAffected
                        ? "0 0 0 3px color-mix(in oklch, var(--warning) 25%, transparent)"
                        : "none",
                    }}
                  />
                  <span style={{ flex: 1 }}>{c}</span>
                  <span
                    style={{ fontSize: 11, color: "var(--muted-foreground)" }}
                  >
                    {isAffected ? "degraded" : "operational"}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>

        {active.length > 0 && (
          <Card>
            <CardHeader
              title="Active incidents"
              description={`${active.length} ongoing`}
            />
            {active.map((inc) => (
              <IncidentDetail key={inc.id} incident={inc} />
            ))}
          </Card>
        )}

        <Card>
          <CardHeader
            title="Past incidents"
            description={`${resolved.length} resolved · all-time`}
            right={
              <Button size="sm" variant="ghost" icon="download">
                Export
              </Button>
            }
          />
          {resolved.map((inc) => (
            <IncidentDetail key={inc.id} incident={inc} collapsed />
          ))}
          {resolved.length === 0 && (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: "var(--muted-foreground)",
                fontSize: 13,
              }}
            >
              No resolved incidents on record.
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function MiniStat({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--muted-foreground)",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          marginTop: 4,
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        {icon && (
          <Icon
            name={icon}
            size={13}
            style={{ color: "var(--muted-foreground)" }}
          />
        )}
        {value}
      </div>
    </div>
  );
}
